import React, { useState, useEffect } from "react";
import { ModalX } from "../../components";
import { Alert, Button, Input, Option, Select, Spinner, Tab, TabPanel, Tabs, TabsBody, TabsHeader, Textarea, IconButton, Chip } from "@material-tailwind/react";
import { CalendarIcon, EyeIcon, ListIcon, UserIcon, TrashIcon, Edit2Icon } from "lucide-react";
import { AddNewMeetingRoomBooking, CancelMeetingRoomBooking, GetMeetingRoomBookings, GetMeetingRooms, UpdateMeetingRoomBooking } from "../../services/backend";
import moment from "moment";
import { minutesToText, Notify, NotifyError } from "../../utils/functions";
import { Calendar, momentLocalizer } from 'react-big-calendar'
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './style.css'
import { useGetUsers } from "../../custom-hooks/use-users";
import Autocomplete from '../../components/autocomplete';
import { Link } from "react-router-dom";
import { STATIC_USER_ROLES, VERIFICATION_TYPES } from "../../utils/enums";
import { useConfirm } from "../../custom-hooks/confirm-dialog";



const localizer = momentLocalizer(moment)

const INITIAL_FORM_DATA_FOR_BOOKING = {
    user: null,
    meetingRoom: '',
    title: '',
    description: '',
    startDate: '',
    slot: '',
}

const formatMeetingRoomBookingTitle = (customerName, rawTitle) => {
    const normalizedName = customerName?.trim();
    const normalizedTitle = rawTitle?.trim();

    if (!normalizedTitle) return normalizedTitle;
    if (!normalizedName) return normalizedTitle;

    const expectedPrefix = `${normalizedName} - `;
    if (normalizedTitle.startsWith(expectedPrefix)) {
        return normalizedTitle;
    }

    return `${normalizedName} - ${normalizedTitle}`;
}

const formatMeetingRoomBookingDescription = (customerName, rawDescription) => {
    const normalizedName = customerName?.trim();
    const normalizedDescription = rawDescription?.trim() || 'No Description';

    if (!normalizedDescription) return normalizedDescription;
    if (!normalizedName) return normalizedDescription;

    const expectedPrefix = `${normalizedName} - `;
    if (normalizedDescription.startsWith(expectedPrefix)) {
        return normalizedDescription;
    }

    return `${normalizedName} - ${normalizedDescription}`;
}

const toRoleLabel = (role) => {
    if (!role) return 'Unknown';
    return role.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

const getCreatorTagText = (booking) => {
    const creatorRole = booking?.createdBy?.role;
    const creatorName = booking?.createdBy?.name;
    const isCreatedByCustomer = booking?.createdBy?._id && booking?.user?._id && String(booking.createdBy._id) === String(booking.user._id);

    if (!creatorName) return null;

    const isCustomerCreated = creatorRole
        ? (creatorRole === STATIC_USER_ROLES.USER || creatorRole === STATIC_USER_ROLES.TEAM_MEMBER)
        : isCreatedByCustomer;
    if (isCustomerCreated) return null;

    if (!creatorRole) {
        return `Created by - ${creatorName}`;
    }

    if (creatorRole === STATIC_USER_ROLES.ADMIN) {
        return `Admin created - ${creatorName}`;
    }

    return `${toRoleLabel(creatorRole)} created - ${creatorName}`;
};

const ReceptionistMeetingRoom = () => {

    // State variables
    const [meetingRooms, setMeetingRooms] = useState([]);
    // const [googleMapsLinkStatus, setGoogleMapsLinkStatus] = useState('');
    // const [isLoadingCoordinates, setIsLoadingCoordinates] = useState(false);
    const [refreshRequired, setRefreshRequired] = useState(0);
    const [loading, setLoading] = useState(true);

    const [bookingFormData, setBookingFormData] = useState(INITIAL_FORM_DATA_FOR_BOOKING);
    const [modalOpenForBooking, setModalOpenForBooking] = useState(false);
    const [meetingRoomBookings, setMeetingRoomBookings] = useState([]);
    const [events, setEvents] = useState([]);
    const [view, setView] = useState('month');
    const [loadingMutation, setLoadingMutation] = useState(false);
    const [isUpdatingBooking, setIsUpdatingBooking] = useState(false);
    const [isViewingPastBooking, setIsViewingPastBooking] = useState(false);
    const [selectedBookingId, setSelectedBookingId] = useState(null);
    const [originalBookingData, setOriginalBookingData] = useState(null);
    const { confirm, ConfirmationDialog } = useConfirm();

    const { data: users, isLoading: isLoadingUsers } = useGetUsers({
        _limit: 1000,
        // role: [STATIC_USER_ROLES.USER]
    })

    useEffect(() => {
        const fetchMeetingRooms = async () => {
            setLoading(true);
            const response = await GetMeetingRooms();
            setMeetingRooms(response.data.meetingRooms);
            setLoading(false);
        }
        const fetchMeetingRoomBookings = async () => {
            const response = await GetMeetingRoomBookings();
            setMeetingRoomBookings(response.data.meetingRoomBookings);
            const events = response.data.meetingRoomBookings.filter((booking) => booking.status !== VERIFICATION_TYPES.CANCELLED).map((booking) => ({
                id: booking._id,
                title: booking.title,
                start: new Date(booking.startDate),
                end: new Date(booking.endDate),
                resourceId: booking.meetingRoom?._id,
                color: booking.meetingRoom?.idColor,
                description: booking.description,
                slot: booking.slot,
                userObj: booking.user,
            }));
            setEvents(events);
        }
        fetchMeetingRoomBookings();
        fetchMeetingRooms();
    }, [refreshRequired]);

    const handleOnTimeSlotSelect = (slotInfo) => {
        if (view === 'month') {
            setView('day');
        }
    };

    const handleOnEventSelect = (event) => {
        const formData = {
            user: event.userObj ? {
                value: event.userObj._id,
                label: <div className='flex items-center justify-between gap-2'>
                    <div className='flex items-center gap-2'>
                        <div className='w-4 h-4 rounded-full'>
                            <UserIcon size={12} />
                        </div>
                        {event.userObj.name}
                    </div>
                    <div className='text-xs text-gray-500'>
                        {event.userObj.email}
                    </div>
                </div>,
                searchLabel: event.userObj.name + ' (' + event.userObj.email + ')',
                userName: event.userObj.name
            } : null,
            meetingRoom: event.resourceId,
            title: event.title,
            description: event.description,
            startDate: event.start,
            slot: String(event.slot),
        };
        setBookingFormData(formData);
        setOriginalBookingData(formData);
        setIsUpdatingBooking(true);
        setIsViewingPastBooking(true);
        setSelectedBookingId(event.id);
        setModalOpenForBooking(true);
    };

    const handleBookingFormSubmit = async (e) => {
        e.preventDefault();
        try {
            if (!bookingFormData.user) return NotifyError('Please select a customer', 'Validation Error');
            if (!bookingFormData.meetingRoom) return NotifyError('Please select a meeting room', 'Validation Error');
            if (!bookingFormData.title) return NotifyError('Please enter a title', 'Validation Error');
            if (!bookingFormData.startDate) return NotifyError('Please select a start time', 'Validation Error');
            if (!bookingFormData.slot) return NotifyError('Please select a duration (slot)', 'Validation Error');

            setLoadingMutation(true);
            const customerName = bookingFormData.user?.userName || '';
            const bookingTitle = formatMeetingRoomBookingTitle(customerName, bookingFormData.title);
            const bookingDescription = formatMeetingRoomBookingDescription(customerName, bookingFormData.description);
            if (isUpdatingBooking) {
                const updatedMeetingRoomBooking = {
                    user: bookingFormData.user?.value || null,
                    meetingRoom: bookingFormData.meetingRoom,
                    title: bookingTitle,
                    description: bookingDescription,
                    startDate: bookingFormData.startDate,
                    slot: bookingFormData.slot,
                };
                await UpdateMeetingRoomBooking(selectedBookingId, updatedMeetingRoomBooking);
                setRefreshRequired(refreshRequired + 1);
                Notify('Meeting Room Booking Updated Successfully', 'success');
            } else {
                const newMeetingRoomBooking = {
                    user: bookingFormData.user?.value || null,
                    meetingRoom: bookingFormData.meetingRoom,
                    title: bookingTitle,
                    description: bookingDescription,
                    startDate: bookingFormData.startDate,
                    slot: bookingFormData.slot,
                };
                await AddNewMeetingRoomBooking(newMeetingRoomBooking);
                setRefreshRequired(refreshRequired + 1);
                Notify('Meeting Room Booked Successfully', 'success');
            }
            setModalOpenForBooking(false);
            setBookingFormData(INITIAL_FORM_DATA_FOR_BOOKING);
            setIsUpdatingBooking(false);
            setIsViewingPastBooking(false);
            setSelectedBookingId(null);
            setOriginalBookingData(null);
        }
        catch (error) {
            console.error(error);
            NotifyError(error.response.data.message, error);
        } finally {
            setLoadingMutation(false);
        }
    }

    const handleCancelBooking = async (bookingId) => {
        const confirmation = await confirm({
            title: 'Are you sure you want to cancel this booking?',
            message: 'This action cannot be undone.',
            confirmText: 'Yes, Cancel',
            cancelText: 'No, Keep it',
            confirmBtnColor: 'red',
        });

        if (confirmation) {
            try {
                await CancelMeetingRoomBooking(bookingId);
                setRefreshRequired(refreshRequired + 1);
                Notify('Meeting Room Booking Cancelled Successfully', 'success');
            }
            catch (error) {
                console.error(error);
                NotifyError(error.response.data.message, error);
            }
        }
    }

    const getAvailableTimeOptions = () => {
        const options = [];
        for (let i = 0; i < 48; i++) {
            const hours = Math.floor(i / 2);
            const minutes = (i % 2) * 30;
            const timeValue = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

            let isAvailable = true;
            if (bookingFormData.meetingRoom && bookingFormData.startDate) {
                const optionTime = moment(bookingFormData.startDate).set({ hour: hours, minute: minutes, second: 0, millisecond: 0 });
                for (const event of events) {
                    if (event.resourceId === bookingFormData.meetingRoom) {
                        if (isUpdatingBooking && event.id === selectedBookingId) continue;
                        const eventStart = moment(event.start);
                        const eventEnd = moment(event.end);
                        if (optionTime.isSameOrAfter(eventStart) && optionTime.isBefore(eventEnd)) {
                            isAvailable = false;
                            break;
                        }
                    }
                }
            }
            if (isAvailable) {
                options.push(
                    <Option key={timeValue} value={timeValue}>
                        {moment().hour(hours).minute(minutes).format('hh:mm A')}
                    </Option>
                );
            }
        }
        return options;
    };

    const normalizeBookingFormDataForCompare = (formData) => ({
        user: formData?.user?.value || formData?.user || null,
        meetingRoom: formData?.meetingRoom || null,
        title: formData?.title || '',
        description: formData?.description || '',
        slot: formData?.slot || '',
        startDate: formData?.startDate ? new Date(formData.startDate).getTime() : null,
    });

    const isFormChanged = isUpdatingBooking ?
        JSON.stringify(normalizeBookingFormDataForCompare(bookingFormData)) !== JSON.stringify(normalizeBookingFormDataForCompare(originalBookingData))
        : true;

    return (
        <div className={`p-4`}>
            <div className="flex justify-between items-center mb-4">
                <h1 className="md:text-2xl font-thin">Meeting Room Calendar</h1>

            </div>
            <div className="p-4 border rounded-3xl">
                <div>
                    <Tabs value="calendar">
                        <TabsHeader className='bg-gray-200 border border-gray-200 shadow-none rounded-2xl'>
                            <Tab value="calendar">
                                <div className='flex items-center gap-2 font-thin'>
                                    <CalendarIcon className='w-4 h-4' /> Calendar
                                </div>
                            </Tab>
                            <Tab value="list">
                                <div className='flex items-center gap-2 font-thin'>
                                    <ListIcon className='w-4 h-4' /> List
                                </div>
                            </Tab>
                        </TabsHeader>
                        <TabsBody>
                            <TabPanel value="calendar">
                                <Calendar
                                    localizer={localizer}
                                    events={events}
                                    eventPropGetter={(event) => {
                                        return {
                                            className: 'text-xs font-bold cursor-pointer',
                                            style: {
                                                backgroundColor: event.color || 'rgb(228, 148, 0)',
                                                borderRadius: '0',
                                                border: '0px solid rgb(228, 148, 0)',
                                            }
                                        }
                                    }}
                                    selectable={view === 'day' || view === 'week' || view === 'month'}
                                    onSelectSlot={handleOnTimeSlotSelect}
                                    onSelectEvent={handleOnEventSelect}
                                    longPressThreshold={10}
                                    onView={setView}
                                    view={view}
                                    startAccessor="start"
                                    endAccessor="end"
                                    style={{ height: 700 }}
                                    resources={meetingRooms.map((meetingRoom) => ({
                                        id: meetingRoom._id,
                                        title: meetingRoom.code
                                    }))}
                                />
                            </TabPanel>
                            <TabPanel value="list">
                                {
                                    loading ?
                                        <div className="flex justify-center">
                                            <Spinner />
                                        </div> : (meetingRoomBookings.length === 0 ?
                                            <div className="flex justify-center mt-4">
                                                <Alert color="brown" variant="outlined">
                                                    Click on the Date to book a Meeting Room.
                                                </Alert>
                                            </div> :
                                            <div className="overflow-x-auto max-h-[700px] overflow-y-auto">
                                                <table className="w-full min-w-max table-auto text-left">
                                                    <thead className="text-sm">
                                                        <tr className="py-3">
                                                            <th colSpan={4} className="py-3 text-xs font-light text-orange-900">List of All Bookings</th>
                                                        </tr>
                                                        <tr className="py-3 uppercase">
                                                            <th className="py-3">Meeting Room</th>
                                                            <th className="py-3">Booked For</th>
                                                            <th className="py-3">Booked By</th>
                                                            <th className="py-3">Booking Time</th>
                                                            <th className="py-3">Booking Duration</th>
                                                            <th className="py-3">Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {meetingRoomBookings.map((meetingRoomBooking, index) => (
                                                            <tr key={index} draggable className="border-t hover:bg-gray-50 button rounded-full">
                                                                <td className="font-thin py-3 text-sm">
                                                                    <div className="text-sm font-bold">
                                                                        <div className="flex items-center gap-1">
                                                                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: meetingRoomBooking.meetingRoom?.idColor || 'gray' }}></div>
                                                                            <div>{meetingRoomBooking.meetingRoom?.code || 'N/A'}</div>
                                                                            <div className="text-xs text-gray-500">
                                                                                {meetingRoomBooking.meetingRoom?.floor?.floorCode || 'N/A'}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="text-xs text-gray-500">
                                                                        {meetingRoomBooking.meetingRoom?.floor?.building?.name} - {meetingRoomBooking.meetingRoom?.floor?.building?.branch?.name}
                                                                    </div>
                                                                </td>
                                                                <td className="py-3 text-sm">
                                                                    {meetingRoomBooking.user ? (
                                                                        <Link to={`/customer/${meetingRoomBooking.user._id}`}
                                                                            className='text-sm font-thin text-blue-600 hover:text-orange-600 duration-300 underline flex items-center gap-1'>
                                                                            <UserIcon size={12} /> {meetingRoomBooking.user.name}
                                                                        </Link>
                                                                    ) : (
                                                                        <span className="text-gray-400 italic">User Deleted</span>
                                                                    )}
                                                                </td>
                                                                <td className="font-thin py-3 text-sm">
                                                                    {getCreatorTagText(meetingRoomBooking) ? (
                                                                        <Chip color='blue-gray' value={getCreatorTagText(meetingRoomBooking)} className='w-fit normal-case' />
                                                                    ) : (
                                                                        <div className='text-xs font-thin text-gray-500'>
                                                                            -
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td className="font-thin py-3 text-sm">
                                                                    <div className='text-sm font-bold'>
                                                                        {moment(meetingRoomBooking.startDate).format('LT')} - {moment(meetingRoomBooking.endDate).format('LT')}
                                                                    </div>
                                                                    <div className='text-xs text-gray-500'>
                                                                        {moment(meetingRoomBooking.createdDate).format('lll')}
                                                                    </div>
                                                                </td>
                                                                <td className="font-thin py-3 text-sm">
                                                                    {minutesToText(meetingRoomBooking.slot)}
                                                                </td>
                                                                <td className="font-thin py-3 text-sm">
                                                                    {
                                                                        meetingRoomBooking.status !== VERIFICATION_TYPES.CANCELLED ?
                                                                            <IconButton size='sm' color='blue-gray' className='rounded-full' onClick={() => {
                                                                                const userObj = meetingRoomBooking.user;
                                                                                const formData = {
                                                                                    user: userObj ? {
                                                                                        value: userObj._id,
                                                                                        label: <div className='flex items-center justify-between gap-2'>
                                                                                            <div className='flex items-center gap-2'>
                                                                                                <div className='w-4 h-4 rounded-full'>
                                                                                                    <UserIcon size={12} />
                                                                                                </div>
                                                                                                {userObj.name}
                                                                                            </div>
                                                                                            <div className='text-xs text-gray-500'>
                                                                                                {userObj.email}
                                                                                            </div>
                                                                                        </div>,
                                                                                        searchLabel: (userObj.name || '') + ' (' + (userObj.email || '') + ')',
                                                                                        userName: userObj.name || ''
                                                                                    } : null,
                                                                                    meetingRoom: meetingRoomBooking.meetingRoom?._id,
                                                                                    title: meetingRoomBooking.title,
                                                                                    description: meetingRoomBooking.description,
                                                                                    startDate: new Date(meetingRoomBooking.startDate),
                                                                                    slot: String(meetingRoomBooking.slot),
                                                                                };
                                                                                setBookingFormData(formData);
                                                                                setOriginalBookingData(formData);
                                                                                setIsUpdatingBooking(true);
                                                                                setIsViewingPastBooking(true);
                                                                                setSelectedBookingId(meetingRoomBooking._id);
                                                                                setModalOpenForBooking(true);
                                                                            }}>
                                                                                <EyeIcon className='w-4 h-4' />
                                                                            </IconButton>
                                                                            :
                                                                            <div className='flex items-center gap-2'>
                                                                                <Chip color='yellow' value='Cancelled' />
                                                                            </div>
                                                                    }
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>

                                                </table>
                                            </div>)
                                }
                            </TabPanel>
                        </TabsBody>
                    </Tabs>
                </div>


                <ModalX
                    isOpen={modalOpenForBooking}
                    onClose={() => setModalOpenForBooking(false)}
                    title={
                        <div className='flex items-center justify-between gap-2 w-full'>
                            <div className='text-lg font-thin'>{isUpdatingBooking ? (moment(bookingFormData.startDate).isAfter(moment()) ? "Booking Details" : "Viewing a past booking") : "Book Meeting Room"}</div>
                            <div className='text-md font-thin'>
                                {moment(bookingFormData.startDate).format('h:mm A')} - {
                                    bookingFormData.slot ?
                                        moment(bookingFormData.startDate).add(bookingFormData.slot, 'minutes').format('h:mm A') :
                                        <span>Slot</span>

                                }
                                <div className='text-xs text-orange-900'>
                                    {moment(bookingFormData.startDate).format('LL')}
                                </div>
                            </div>
                        </div>
                    }
                    maxWidth="xl">
                    <form onSubmit={handleBookingFormSubmit}>
                        <div className="space-y-2">
                            <div>
                                <Autocomplete
                                    placeholder="Select Customer"
                                    isLoading={isLoadingUsers}
                                    isDisabled={isViewingPastBooking}
                                    options={
                                        users?.users?.map((user) => ({
                                            value: user._id,
                                            userName: user.name,
                                            label: <div className='flex items-center justify-between gap-2'>
                                                <div className='flex items-center gap-2'>
                                                    <div className='w-4 h-4 rounded-full'>
                                                        <UserIcon size={12} />
                                                    </div>
                                                    {user.name}
                                                </div>
                                                <div className='text-xs text-gray-500'>
                                                    {user.email}
                                                </div>
                                            </div>,
                                            searchLabel: user.name + ' (' + user.email + ')'
                                        })) || []}
                                    isClearable={true}
                                    required={true}
                                    getOptionLabel={(option) => option.label}
                                    getOptionValue={(option) => option.searchLabel}
                                    onChange={(newValue) => setBookingFormData({ ...bookingFormData, user: newValue })}
                                    value={bookingFormData.user}
                                />
                            </div>
                            <div>
                                <Select
                                    label="Select Meeting Room"
                                    value={bookingFormData.meetingRoom}
                                    disabled={isViewingPastBooking}
                                    onChange={newValue => setBookingFormData({ ...bookingFormData, meetingRoom: newValue })}
                                >
                                    {
                                        meetingRooms.map((meetingRoom, index) => (
                                            <Option key={index} value={meetingRoom._id}>
                                                {meetingRoom.number} ({meetingRoom.code}) in {meetingRoom.floor.building.name}
                                            </Option>
                                        ))
                                    }
                                </Select>
                            </div>
                            <div>
                                <Input
                                    label="Title"
                                    name="title"
                                    disabled={isViewingPastBooking}
                                    value={bookingFormData.title}
                                    onChange={(e) => setBookingFormData({ ...bookingFormData, title: e.target.value })}
                                />
                            </div>
                            <div>
                                <Textarea
                                    label="Description"
                                    name="description"
                                    disabled={isViewingPastBooking}
                                    value={bookingFormData.description}
                                    onChange={(e) => setBookingFormData({ ...bookingFormData, description: e.target.value })}
                                />
                            </div>
                            <div className="flex gap-2 flex-col md:flex-row">
                                <div className="flex-1">
                                    <Select
                                        label="Start Time"
                                        value={bookingFormData.startDate ? moment(bookingFormData.startDate).format("HH:mm") : ""}
                                        disabled={isViewingPastBooking}
                                        menuProps={{ className: "max-h-60" }}
                                        onChange={(newValue) => {
                                            if (newValue) {
                                                const [hours, minutes] = newValue.split(':');
                                                const newStartDate = moment(bookingFormData.startDate).set({ hour: parseInt(hours), minute: parseInt(minutes), second: 0, millisecond: 0 }).toDate();
                                                setBookingFormData({ ...bookingFormData, startDate: newStartDate });
                                            }
                                        }}
                                    >
                                        {getAvailableTimeOptions()}
                                    </Select>
                                </div>
                                <div className="flex-1">
                                    <Select
                                        label="Slot"
                                        value={bookingFormData.slot}
                                        disabled={isViewingPastBooking}
                                        onChange={newValue => setBookingFormData({ ...bookingFormData, slot: newValue })}
                                    >
                                        <Option value={'30'}>30 Minutes</Option>
                                        <Option value={'60'}>1 Hour</Option>
                                        <Option value={'90'}>1 Hour, 30 Minutes</Option>
                                        <Option value={'120'}>2 Hours</Option>
                                    </Select>
                                </div>
                            </div>
                        </div>
                        <div className="mt-4 flex justify-end space-x-2 items-center w-full">
                            {isUpdatingBooking && !isViewingPastBooking && (
                                <div className="flex-1">
                                    <Button
                                        onClick={() => handleCancelBooking(selectedBookingId).then(() => { setModalOpenForBooking(false); setIsUpdatingBooking(false); })}
                                        color="red"
                                        className="rounded-full flex gap-2 items-center"
                                        disabled={loadingMutation}
                                        variant="outlined"
                                        size="sm"
                                    >
                                        <TrashIcon size={14} /> Cancel Booking
                                    </Button>
                                </div>
                            )}
                            <Button
                                onClick={() => {
                                    setModalOpenForBooking(false);
                                    setIsUpdatingBooking(false);
                                    setIsViewingPastBooking(false);
                                    setBookingFormData(INITIAL_FORM_DATA_FOR_BOOKING);
                                    setSelectedBookingId(null);
                                    setOriginalBookingData(null);
                                }}
                                className="rounded-full "
                                disabled={loadingMutation}
                                variant="text"
                            >
                                Close
                            </Button>
                            {!isViewingPastBooking && (
                                <Button type="submit" className="rounded-full" disabled={loadingMutation || !isFormChanged} loading={loadingMutation}>
                                    {isUpdatingBooking ? "Update Booking" : "Book"}
                                </Button>
                            )}
                        </div>
                    </form>
                </ModalX>
            </div>
            <div className={`p-4 border rounded-3xl mt-2`}>
                <div className="flex justify-between items-center mb-4">
                    <h1 className="md:text-2xl font-thin">Meeting Rooms</h1>
                </div>
                {
                    loading ?
                        <div className="flex justify-center">
                            <Spinner />
                        </div> : (meetingRooms.length === 0 ?
                            <div className="flex justify-center mt-4">
                                <Alert color="brown" variant="outlined">
                                    Click on the button to add a Meeting Room.
                                </Alert>
                            </div> :
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-max table-auto text-left">
                                    <thead className="text-sm">
                                        <tr className="py-3">
                                            <th colSpan={4} className="py-3 text-xs font-light text-orange-900">List of All Meeting Rooms</th>
                                            {/* <th colSpan={2} className="py-3">
                                    <Input
                                        label="Search"
                                    />
                                </th> */}
                                        </tr>
                                        <tr className="py-3 uppercase">
                                            <th className="py-3">#</th>
                                            <th className="py-3">Location</th>
                                            <th className="py-3">Status</th>
                                            <th className="py-3">Modified</th>
                                            <th className="py-3">Created</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {meetingRooms.map((meetingRoom, index) => (
                                            <tr key={index} draggable className="border-t hover:bg-gray-50 button rounded-full">
                                                <td className="font-thin py-3 text-sm">
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-4 h-4 rounded-full" style={{ backgroundColor: meetingRoom.idColor }}></div>
                                                        {meetingRoom.number} ({meetingRoom.code})

                                                    </div>
                                                </td>
                                                <td className="font-thin py-3 text-sm">
                                                    <div className="">
                                                        {meetingRoom.floor.number} ({meetingRoom.floor.floorCode}) - {meetingRoom.floor.building.name}
                                                    </div>
                                                    <div className="">
                                                        {meetingRoom.floor.building.branch.name}
                                                    </div>
                                                </td>
                                                <td className="font-thin py-3 text-sm">
                                                    {meetingRoom.status}
                                                </td>
                                                <td className="font-thin py-3 text-sm">
                                                    {moment(meetingRoom.lastUpdated).format('lll')}
                                                </td>
                                                <td className="font-thin py-3 text-sm">
                                                    {moment(meetingRoom.createdDate).format('lll')}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>

                                </table>
                            </div>)
                }
            </div>
            <ConfirmationDialog />
        </div>
    );
};

export default ReceptionistMeetingRoom;
